<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    class="absolute right-0 z-20 mt-2 w-72 origin-top-right border border-gray-200 rounded-lg bg-white shadow-lg focus:outline-none overflow-hidden"
    role="menu"
    aria-orientation="vertical"
    aria-labelledby="menu-button"
    tabindex="-1"
  >
    <div role="none">
      <template v-for="(option, index) in options" :key="option.value">
        <div
          v-if="index > 0 && option.group !== options[index - 1].group"
          class="border-t border-gray-200"
        ></div>

        <a
          href="#"
          @click.prevent="selectOption(option.value)"
          :class="[
            'flex items-center px-4 py-3 text-sm transition-colors',
            selectedValue === option.value
              ? 'bg-gray-200 text-gray-900'
              : 'text-gray-700 hover:bg-gray-200'
          ]"
          role="menuitem"
          tabindex="-1"
        >
          <svg
            v-if="option.direction === 'asc'"
            class="w-6 h-6 mr-2 text-gray-600"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7.03003 13.92H11.03V4.99997L13.04 4.96997V13.92H17.03L12.03 18.92L7.03003 13.92Z"
              fill="#4B5563"
            />
          </svg>

          <svg
            v-else
            class="w-6 h-6 mr-2 text-gray-600"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7.03003 9.96997H11.03V18.89L13.04 18.92V9.96997H17.03L12.03 4.96997L7.03003 9.96997Z"
              fill="#4B5563"
            />
          </svg>

          <span class="ml-6">{{ option.label }}</span>
        </a>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, PropType } from 'vue'

interface SortOption {
  label: string
  value: string
  direction: 'asc' | 'desc'
  group: string
}

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  options: {
    type: Array as PropType<SortOption[]>,
    required: true
  },
  selectedValue: {
    type: String,
    required: true
  }
})

const emit = defineEmits<{
  (e: 'select', value: string): void
  (e: 'update:isOpen', value: boolean): void
}>()

const modalRef = ref<HTMLElement | null>(null)

function selectOption(value: string) {
  emit('select', value)
}

const handleClickOutside = (event: MouseEvent) => {
  if (modalRef.value && !modalRef.value.contains(event.target as Node)) {
    emit('update:isOpen', false)
  }
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>
